import '@vitejs/plugin-react/preamble'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { trackPageView } from '../lib/analytics'

// Variable para mantener la referencia del root (necesaria para HMR)
let root: ReturnType<typeof createRoot> | null = null

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    // eager: false → code splitting por ruta: cada página en su propio chunk (evita duplicación)
    const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx')
    const importFn = pages[`../pages/${name}.tsx`]
    if (!importFn) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      throw new Error(`Inertia page component not found: '${name}.tsx'`)
    }
    return importFn()
  },

  setup({ el, App, props }) {
    // Reutiliza el root existente para HMR, o crea uno nuevo
    if (!root) {
      root = createRoot(el)
    }

    root.render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )

    // Trackea la página inicial
    trackPageView(window.location.pathname, document.title)

    // Trackea las navegaciones de Inertia usando eventos del DOM
    const handleInertiaFinish = () => {
      // Pequeño delay para asegurar que el título se haya actualizado
      setTimeout(() => {
        trackPageView(window.location.pathname, document.title)
      }, 100)
    }

    document.addEventListener('inertia:finish', handleInertiaFinish)

    // Evita canonical duplicado: el layout ERB inyecta uno para crawlers; Inertia gestiona el <Head> en navegaciones SPA.
    const removeServerCanonicalOnce = () => {
      document.querySelector('link[data-seo-server-canonical]')?.remove()
    }
    document.addEventListener('inertia:start', removeServerCanonicalOnce, { once: true })
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})

// Habilita HMR para este módulo
if (import.meta.hot) {
  import.meta.hot.accept()
}
